/**
 * SharedCoi: public read-only view of a shared community of interest
 * (/p/coi/:token).
 *
 * No login required to view or co-sign -- the share token is the capability.
 * Making a copy or submitting requires a free account (the copy lands in
 * the signer's drafts as an editable COI they can adjust and submit).
 * Actions in friction order:
 *
 *   1. Co-sign ("I support this community") -- one tap, public advocacy.
 *   2. Make a copy -- fork into your own editable draft, then adjust.
 *   3. Submit as-is -- forks, then drops you in the COI editor at the
 *      submit step.
 *
 * Mobile-first: most share links travel over text/WhatsApp.
 */
import { ExportMenu } from '../components/ExportMenu';
import { exportCoi } from '../lib/exports';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../lib/api';
import { CoiBoundaryMap, COI_BOUNDARY_COLOR } from '../components/coi/CoiBoundaryMap';
import { MapLegend } from '../components/map/MapLegend';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useTenantStore } from '../stores/tenant';
import {
  CoiCosignDuplicateError,
  cosignCoi,
  forkCoi,
  getSharedCoi,
  type SharedCoi as SharedCoiData,
} from '../lib/coiSharing';

function hasPolygon(polygon: Record<string, unknown>): boolean {
  const type = (polygon as { type?: unknown }).type;
  const coords = (polygon as { coordinates?: unknown }).coordinates;
  return (
    (type === 'Polygon' || type === 'MultiPolygon') &&
    Array.isArray(coords) &&
    coords.length > 0
  );
}

function CosignModal({
  communityName,
  onClose,
  onSubmit,
  submitting,
  error,
}: {
  communityName: string;
  onClose: () => void;
  onSubmit: (displayName: string, contact: string) => void;
  submitting: boolean;
  error: string | null;
}) {
  const [displayName, setDisplayName] = useState('');
  const [contact, setContact] = useState('');
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coi-cosign-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="coi-cosign-title" className="text-base font-semibold text-ink">
          Co-sign this community
        </h2>
        <p className="mt-1 text-sm text-stone-600">
          Your name is added to the public supporter list for
          {communityName ? ` “${communityName}”` : ' this community'}.
        </p>
        <label className="mt-4 block text-sm text-stone-700">
          Your name
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Maria Santos"
            maxLength={120}
            className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
            autoFocus
          />
        </label>
        <label className="mt-3 block text-sm text-stone-700">
          Email <span className="text-stone-400">(optional — lets staff reach you)</span>
          <input
            type="email"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="you@example.org"
            maxLength={320}
            className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
          />
        </label>
        {error && (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting || displayName.trim().length === 0}
            onClick={() => onSubmit(displayName.trim(), contact.trim())}
            className="rounded-lg bg-tenant-primary px-4 py-2 text-sm font-medium text-white hover:bg-tenant-strong disabled:opacity-50"
          >
            {submitting ? 'Adding…' : 'Add my support'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SharedCoi(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenant = useTenantStore((s) => s.tenant);
  document.title = 'Shared community of interest';

  const [cosignOpen, setCosignOpen] = useState(false);
  const [cosigning, setCosigning] = useState(false);
  const [cosignError, setCosignError] = useState<string | null>(null);
  const [cosigned, setCosigned] = useState(false);
  const [forking, setForking] = useState(false);
  const [forkError, setForkError] = useState<string | null>(null);
  const { requireAuth, signInModal } = useRequireAuth();

  const coiQuery = useQuery({
    queryKey: ['shared-coi', token],
    queryFn: () => getSharedCoi(token ?? ''),
    retry: false,
    enabled: !!token,
  });

  const coi = coiQuery.data ?? null;
  const notFound = coiQuery.error instanceof ApiError && coiQuery.error.status === 404;

  async function handleCosign(displayName: string, contact: string): Promise<void> {
    if (!token) return;
    setCosigning(true);
    setCosignError(null);
    try {
      const count = await cosignCoi(token, displayName, contact);
      queryClient.setQueryData<SharedCoiData | undefined>(
        ['shared-coi', token],
        (old) => (old ? { ...old, cosign_count: count } : old),
      );
      setCosigned(true);
      setCosignOpen(false);
    } catch (error) {
      setCosignError(
        error instanceof CoiCosignDuplicateError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Could not add your co-sign. Please try again.',
      );
    } finally {
      setCosigning(false);
    }
  }

  async function handleFork(submitAfter: boolean): Promise<void> {
    if (!token || !coi || forking) return;
    setForking(true);
    setForkError(null);
    try {
      const draft = await forkCoi(token);
      navigate('/coi', {
        state: {
          draftCoiId: draft.coi_id,
          forkedFrom: coi.title ?? 'a shared community',
          hint: submitAfter
            ? 'This is a copy of the shared community — review it, then hit Submit community when ready.'
            : 'Copied — adjust the boundary and description, then submit when ready.',
        },
      });
    } catch (error) {
      setForkError(
        error instanceof Error ? error.message : 'Could not copy this community. Please try again.',
      );
    } finally {
      setForking(false);
    }
  }

  if (!tenant) return <p className="p-6 text-sm text-stone-500">Loading…</p>;

  if (coiQuery.isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="text-sm text-stone-500">Loading the shared community…</p>
      </div>
    );
  }

  if (notFound || coiQuery.isError || !coi) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-xl font-semibold text-ink">This link doesn&apos;t work</h1>
        <p className="mt-2 text-sm text-stone-600">
          The share link is invalid or was revoked by the community&apos;s author.
        </p>
        <Link
          to="/"
          className="mt-6 inline-block rounded-lg bg-tenant-primary px-4 py-2 text-sm font-medium text-white hover:bg-tenant-strong"
        >
          Go to {tenant.name}&apos;s homepage
        </Link>
      </div>
    );
  }

  const showMap = hasPolygon(coi.polygon_geojson);

  return (
    <div className="mx-auto max-w-5xl space-y-4 px-4 py-6">
      {signInModal}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
          Shared community of interest · {tenant.name}
        </p>
        <h1 className="mt-1 text-2xl font-bold text-ink">
          {coi.title ?? 'Untitled community'}
        </h1>
        <p className="mt-1 text-sm text-stone-600">
          Shared by a community member ·{' '}
          <span className="font-medium text-stone-800">
            {coi.cosign_count} co-sign{coi.cosign_count === 1 ? '' : 's'}
          </span>{' '}
          · {coi.fork_count} {coi.fork_count === 1 ? 'copy' : 'copies'} made
        </p>
        <ExportMenu
          className="mt-3 inline-block"
          variant="secondary"
          label="Download"
          onExport={(format) =>
            exportCoi(format, {
              title: coi.title ?? 'Community of interest',
              narrative: coi.narrative,
              interests: coi.interests,
              polygon: coi.bbox ? coi.polygon_geojson : null,
            })
          }
          unavailable={
            coi.bbox
              ? {}
              : { shapefile: 'This community has no map boundary', equivalency: 'This community has no map boundary' }
          }
        />
      </div>

      <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
        {showMap ? (
          <>
            <CoiBoundaryMap polygon={coi.polygon_geojson} className="h-[55vh] min-h-[320px] w-full" />
            <MapLegend
              items={[{ color: COI_BOUNDARY_COLOR, label: 'Community boundary' }]}
              storageKey="map-legend:shared-coi"
              className="absolute bottom-3 left-3 z-20"
            />
          </>
        ) : (
          <div className="flex h-48 items-center justify-center bg-stone-50 px-6 text-center">
            <p className="max-w-md text-sm text-stone-500">
              This community was described in words — its author didn&apos;t draw a map
              boundary.
            </p>
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-ink">About this community</h2>
          <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-stone-700">
            {coi.narrative}
          </p>
          {coi.interests && (
            <p className="mt-3 text-sm text-stone-600">
              <span className="font-medium text-stone-800">Shared interests: </span>
              {coi.interests}
            </p>
          )}
          {coi.sketch_provenance && (
            <p className="mt-3 rounded-lg bg-tenant-soft px-3 py-2 text-xs text-tenant-strong">
              Boundary drafted with the map sketch assistant and reviewed by the author.
            </p>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-ink">Support this community</h2>
            {cosigned ? (
              <p role="status" className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                ✓ Thanks — your co-sign was added.
              </p>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setCosignError(null);
                  setCosignOpen(true);
                }}
                className="mt-3 w-full rounded-lg bg-tenant-primary px-4 py-2 text-sm font-medium text-white hover:bg-tenant-strong"
              >
                I support this community
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                requireAuth(
                  () => void handleFork(false),
                  'Sign in to copy this community into your drafts and adjust it.',
                )
              }
              disabled={forking}
              className="mt-2 w-full rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
            >
              {forking ? 'Copying…' : 'Make a copy to edit'}
            </button>
            <button
              type="button"
              onClick={() =>
                requireAuth(
                  () => void handleFork(true),
                  'Sign in to copy this community and submit it as your own.',
                )
              }
              disabled={forking}
              className="mt-2 w-full rounded-lg px-4 py-2 text-sm font-medium text-tenant-primary underline-offset-2 hover:underline disabled:opacity-50"
            >
              Submit this community as-is
            </button>
            {forkError && (
              <p role="alert" className="mt-2 text-sm text-red-600">
                {forkError}
              </p>
            )}
            <p className="mt-3 text-xs leading-relaxed text-stone-500">
              Co-signs are public shows of support — they are <em>not</em> official
              submissions to {tenant.name}. Making a copy lets you adjust the
              boundary and description and submit the result yourself.
            </p>
          </div>
        </div>
      </div>

      {cosignOpen && (
        <CosignModal
          communityName={coi.title ?? ''}
          onClose={() => setCosignOpen(false)}
          onSubmit={(name, contact) => void handleCosign(name, contact)}
          submitting={cosigning}
          error={cosignError}
        />
      )}
    </div>
  );
}
